import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { handleUnexpectedError } from '@/app/errors';
import { useAppForm } from '@/app/lib/tanstack-form';
import { routeMetadata } from '@/app/lib/tanstack-router';
import {
  getAccountDetails,
  getTapEnrollmentSetting,
  updatePaperlessBillingSetting,
  updateTapEnrollmentSetting
} from '@/app/network/firefly-api';
import { Account } from '@/app/network/models/v1/account';
import { Button, ButtonGroup, ButtonGroupSpacer } from '@/common/components/button';
import { CheckboxField, CheckboxGroup } from '@/common/components/checkbox';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '@/common/components/dialog';
import { Field, Label } from '@/common/components/fieldset';
import { FormCheckbox } from '@/common/components/form-checkbox';
import { Heading, Subheading } from '@/common/components/heading';
import { Input } from '@/common/components/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/common/components/tooltip';
import { CircleInfoIcon } from '@/icons/fa/solid';
import {
  Setting,
  SettingContent,
  SettingName,
  Settings,
  SettingsSection,
  SettingsSet
} from '@/settings/components/settings';
import { SettingsSidebar } from '@/settings/components/settings-sidebar';
import {
  membershipQueryKeys,
  usePaperlessBillingSetting,
  useUnlinkMembership,
  useUpdateMembershipAlias
} from '@/settings/queries';
import { getAccountLabel } from '@/shared/accounts';
import { accountKeys } from '@/shared/accounts/queries';
import { ContentSection } from '@/shared/components/content-section';
import { TapTermsDialog } from '@/shared/components/tap-terms-dialog';
import { Toast } from '@/shared/components/toast';
import { getMembershipLabel } from '@/shared/memberships';
import { userKeys } from '@/shared/user/queries';

export const Route = createFileRoute('/settings/_layout/memberships/$memberNumber')({
  head: () => ({
    meta: routeMetadata('Membership Details')
  }),
  loader: async ({ params, context }) => {
    const parsedMemberNumber = parseInt(params.memberNumber, 10);

    if (Number.isNaN(parsedMemberNumber)) throw notFound();

    const membership = context.user?.memberships?.find(
      it => it.member_number === parsedMemberNumber
    );

    if (!membership) throw notFound();

    return { membership };
  },
  component: MembershipPage
});

function MembershipPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { membership } = Route.useLoaderData();
  const { accounts: userAccounts } = Route.useRouteContext().user;
  const membershipAccounts = userAccounts.filter(account => account.member_number === membership.member_number);

  const [membershipAlias, setMembershipAlias] = useState<string | undefined>(membership.alias);
  const [updateAliasDialogIsOpen, setUpdateAliasDialogIsOpen] = useState(false);
  const [unlinkMembershipDialogIsOpen, setUnlinkMembershipDialogIsOpen] = useState(false);
  const [tapTermsDialogIsOpen, setTapTermsDialogIsOpen] = useState(false);

  const { data: paperlessBilling } = usePaperlessBillingSetting(membership.member_number);

  const updateMembershipAliasMutation = useUpdateMembershipAlias();

  const updateMembershipOptionsMutation = useMutation({
    mutationFn: async ({ enrollInPaperlessBilling, tapEnrollments }: { enrollInPaperlessBilling: boolean; tapEnrollments: { accountNumber: number; enroll: boolean }[] }) => {
      await updatePaperlessBillingSetting(membership.member_number, enrollInPaperlessBilling);

      for (const tapEnrollment of tapEnrollments) {
        await updateTapEnrollmentSetting(tapEnrollment.accountNumber, tapEnrollment.enroll);
      }
    },
    onSuccess: async () => {
      toast.success(Toast, {
        data: {
          title: 'Settings Updated',
          content: `The settings for membership ${getMembershipLabel(membership)} have been updated.`
        }
      });

      await queryClient.invalidateQueries({ queryKey: membershipQueryKeys.all });
      await queryClient.invalidateQueries({ queryKey: accountKeys.all });

      await navigate({
        to: '/settings/memberships'
      });
    },
    onError: error => handleUnexpectedError(error)
  });

  const unlinkMembershipMutation = useUnlinkMembership();
  const membershipTapEnrollments = useMembershipTapEnrollment(membershipAccounts);

  const optionsForm = useAppForm({
    defaultValues: {
      paperlessBilling: paperlessBilling?.value ?? false,
      tapEnrollment: membershipAccounts.map(account => ({
        accountNumber: account.account_number,
        enrolled: membershipTapEnrollments?.[account.account_number]?.isEnrolledInTap ?? false
      }))
    },
    onSubmit: async ({ value }) => {
      await updateMembershipOptionsMutation.mutateAsync({
        enrollInPaperlessBilling: value.paperlessBilling,
        tapEnrollments: value.tapEnrollment.map(enrollment => ({
          accountNumber: enrollment.accountNumber,
          enroll: enrollment.enrolled
        }))
      });
    }
  });

  const handleUpdateAlias = () => {
    setUpdateAliasDialogIsOpen(true);
  };

  const handleUpdateAliasConfirm = async () => {
    await updateMembershipAliasMutation.mutateAsync(
      {
        memberNumber: membership.member_number,
        alias: membershipAlias
      },
      {
        onSuccess: async () => {
          setUpdateAliasDialogIsOpen(false);

          await queryClient.invalidateQueries({
            queryKey: userKeys.authenticated()
          });

          toast.success(Toast, {
            data: {
              title: 'Nickname Updated',
              content: `The nickname for membership ${getMembershipLabel(membership)} has been updated.`
            }
          });
        },
        onError: error => handleUnexpectedError(error)
      }
    );
  };

  const handleUnlinkMembershipClick = () => {
    setUnlinkMembershipDialogIsOpen(true);
  };

  const handleUnlinkMembershipConfirm = async () => {
    await unlinkMembershipMutation.mutateAsync(
      { memberNumber: membership.member_number },
      {
        onSuccess: async () => {
          setUnlinkMembershipDialogIsOpen(false);

          await queryClient.invalidateQueries({
            queryKey: userKeys.authenticated()
          });

          await navigate({
            to: '/settings/memberships'
          });

          toast.success(Toast, {
            data: {
              title: 'Membership Unlinked',
              content: `Membership has been unlinked.`
            }
          });
        },
        onError: error => handleUnexpectedError(error)
      }
    );
  };

  const handleSaveOptionsClick = async () => {
    const { values } = optionsForm.state;
    const hasPendingTapEnrollments = values.tapEnrollment
      .some(enrollment => enrollment.enrolled && !membershipTapEnrollments?.[enrollment.accountNumber]?.isEnrolledInTap);

    if (hasPendingTapEnrollments) {
      setTapTermsDialogIsOpen(true);
      return;
    }

    await optionsForm.handleSubmit();
  };

  const agreeToTapTerms = async () => {
    setTapTermsDialogIsOpen(false);
    await optionsForm.handleSubmit();
  };

  return (
    <main className="flex flex-col md:flex-row h-full w-full p-0">
      <SettingsSidebar active="memberships"/>
      <ContentSection bg="white">
        <Heading>Membership Details</Heading>

        <Settings>
          <SettingsSection>
            <SettingsSet>
              <Setting>
                <SettingName valign="center">Member Number</SettingName>
                <SettingContent>
                  {membership.member_number}
                </SettingContent>
              </Setting>

              <Setting>
                <SettingName valign="center">Nickname</SettingName>
                <SettingContent>
                  {membership.alias ?? <>&mdash;</>}
                  <Button plain onClick={handleUpdateAlias}>Update</Button>
                </SettingContent>
              </Setting>
            </SettingsSet>
          </SettingsSection>

          <SettingsSection>
            <Subheading>Options</Subheading>

            <SettingsSet>
              <Setting>
                <SettingName>TAP Enrollment</SettingName>
                <SettingContent className="items-start">
                  <optionsForm.AppField name="tapEnrollment" mode="array">
                    {field => (
                      <CheckboxGroup>
                        {field.state.value.map((enrollment, idx) => {
                          const account = membershipAccounts.find(account => account.account_number === enrollment.accountNumber)!;
                          const accountEnrollment = membershipTapEnrollments?.[enrollment.accountNumber];
                          const { isEligibleForTap, isPending } = accountEnrollment ?? { isEligibleForTap: false, isPending: false };

                          return (
                            <optionsForm.AppField name={`tapEnrollment[${idx}].enrolled`} key={enrollment.accountNumber}>
                              {() => (
                                <div className="flex gap-3 items-center">
                                  <CheckboxField disabled={!isEligibleForTap && !isPending}>
                                    <FormCheckbox />
                                    <Label>{getAccountLabel(account)}</Label>
                                  </CheckboxField>
                                  {(!isEligibleForTap && !accountEnrollment.isPending) && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <CircleInfoIcon className="text-zinc-500 size-5" />
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        This account is not eligible for TAP enrollment.
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                              )}
                            </optionsForm.AppField>
                          );
                        })}
                      </CheckboxGroup>
                    )}
                  </optionsForm.AppField>
                  <Button
                    href="https://www.talquinelectric.com/member-services/talquin-assistance-program-tap/"
                    plain
                    target="_blank"
                  >
                    What is TAP?
                  </Button>
                </SettingContent>
              </Setting>
            </SettingsSet>
          </SettingsSection>
        </Settings>

        <ButtonGroup>
          <Button color="danger" onClick={handleUnlinkMembershipClick}>Unlink Membership</Button>
          <ButtonGroupSpacer/>
          <Button plain to="/settings/memberships">Cancel</Button>
          <optionsForm.Subscribe selector={state => state.isSubmitting}>
            {isSubmitting => (
              <Button onClick={handleSaveOptionsClick} disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            )}
          </optionsForm.Subscribe>
        </ButtonGroup>
      </ContentSection>

      <Dialog open={updateAliasDialogIsOpen} onClose={setUpdateAliasDialogIsOpen}>
        <DialogTitle>Update Nickname</DialogTitle>
        <DialogDescription>
          You may choose an optional nickname for this membership. If left blank, the default label will be the member number.
        </DialogDescription>
        <DialogBody>
          <Field>
            <Label>Nickname</Label>
            <Input
              placeholder=""
              value={membershipAlias}
              onChange={event => setMembershipAlias(event.target.value)}
            />
          </Field>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setUpdateAliasDialogIsOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleUpdateAliasConfirm}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unlinkMembershipDialogIsOpen} onClose={setUnlinkMembershipDialogIsOpen}>
        <DialogTitle>Unlink Membership</DialogTitle>
        <DialogDescription>
          Unlinking this membership will remove it and the accounts it contains from your user account. Are you sure you wish to continue?
        </DialogDescription>
        <DialogActions>
          <Button plain onClick={() => setUnlinkMembershipDialogIsOpen(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleUnlinkMembershipConfirm}>Unlink Membership</Button>
        </DialogActions>
      </Dialog>

      <TapTermsDialog isOpen={tapTermsDialogIsOpen} onClose={setTapTermsDialogIsOpen} onAgreeToTerms={agreeToTapTerms} />
    </main>
  );
}

function useMembershipTapEnrollment(accounts: Account[]) {
  const tapEnrollmentQueries = useQueries({
    queries: accounts.map(account => ({
      queryKey: accountKeys.tapEnrollmentById(account.account_number),
      queryFn: async () => await getTapEnrollmentSetting(account.account_number)
    }))
  });

  const accountDetailsQueries = useQueries({
    queries: accounts.map(account => ({
      queryKey: accountKeys.byId(account.account_number),
      queryFn: async () => await getAccountDetails(account.account_number)
    }))
  });

  return Object.fromEntries(
    accounts.map((account, index) => [
      account.account_number,
      {
        isEligibleForTap: accountDetailsQueries[index]?.data?.can_enroll_in_tap ?? false,
        isEnrolledInTap: tapEnrollmentQueries[index]?.data?.value ?? false,
        isPending: tapEnrollmentQueries[index]?.isPending || accountDetailsQueries[index]?.isPending
      }
    ]));
}
